import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from '../../engine/api/client';
import { Button } from '../../engine/design/primitives/Button';
import { Checkbox, Field, Input, Textarea } from '../../engine/design/primitives/Input';
import { Badge, Banner, Money } from '../../engine/design/primitives/Misc';
import { Dialog } from '../../engine/design/primitives/Overlay';
import { Icon } from '../../engine/design/icons';
import { RichTextView } from '../../engine/fields/RichText';
import { formatDate, formatQty, formatRelativeDays } from '../../lib/format';
import { computeTotals, INTERVALS, INTERVAL_LABEL, INTERVAL_SHORT, isChoosable, minor, selectionProblems, type Interval } from '../../modules/quoting/totals';
import '../../modules/quoting/quoting.css';

// The customer's view of a quote, shared by the public acceptance page (/q/:token, where the
// URL token is the credential) and the customer portal (/portal/quotes/:id, where the signed-in
// contact is). Both render the same document: optional lines / pick-one sections, live totals
// mirroring the server's rule, accept (typed-name signature + email + terms) or decline. The
// page that hosts it supplies the endpoints; every figure is re-derived server-side.

export interface PLine {
  id: string;
  section_id: string | null;
  description: string;
  qty: string;
  unit_price_minor: string;
  discount_percent: string;
  tax_rate_percent: string | null;
  kind: 'one_off' | 'recurring';
  recurring_interval: string | null;
  is_optional: boolean;
  is_selected: boolean;
  line_total_minor: string;
}
export interface PSection {
  id: string;
  title: string;
  description: string | null;
  selection: 'all' | 'single' | 'multi';
  is_optional: boolean;
  lines: PLine[];
}
export interface PublicView {
  quote: {
    id: string; ref: string; revision: number; title: string; intro: string | null; terms: string | null; status: string; currency: string;
    expires_at: string | null; sent_at: string | null; accepted_at: string | null; declined_at: string | null; declined_reason: string | null;
    requires_countersign: boolean; countersigned_at: string | null; discount_type: 'none' | 'percent' | 'amount'; discount_value: string; tax_mode: 'exclusive' | 'inclusive' | 'none';
    accepted_by: { name: string | null; email: string | null; accepted_at: string | null } | null;
    tenant: { name: string | null }; client: { name: string | null }; contact: { name: string | null; email: string | null }; owner: { name: string | null; email: string | null };
    sections: PSection[]; lines: PLine[];
  };
  token: { kind: string; expires_at: string | null; used_at: string | null; can_accept: boolean; can_decline: boolean };
}

export interface AcceptBody {
  signer_name: string;
  signer_email: string;
  accept_terms: boolean;
  selected_line_ids: string[];
  signature_text: string;
}

/** What the hosting page provides: where accept / decline go, and where the printable version lives. */
export interface QuoteEndpoints {
  accept: (body: AcceptBody) => Promise<PublicView>;
  decline: (body: { reason: string | null }) => Promise<PublicView>;
  /** The printable version: a link the browser can open on its own (a token page)… */
  printUrl?: string;
  /** …or a handler that fetches it with the signed-in session (the portal); null hides Print. */
  onPrint?: (() => void) | null;
}

export function QuoteBody({ endpoints, data, onChange }: { endpoints: QuoteEndpoints; data: PublicView; onChange: (v: PublicView) => void }) {
  const { quote: q, token: tok } = data;
  const cur = q.currency;
  const sectionOf = useMemo(() => new Map(q.sections.map((s) => [s.id, s] as const)), [q.sections]);
  const choosable = (l: PLine) => isChoosable(l, l.section_id ? sectionOf.get(l.section_id) : undefined);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(q.lines.filter((l) => l.is_selected && choosable(l)).map((l) => l.id)));
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [form, setForm] = useState({ signer_name: q.contact.name ?? '', signer_email: q.contact.email ?? '', accept_terms: false });
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const open = tok.can_accept && (q.status === 'sent' || q.status === 'viewed');
  const lines = useMemo(() => q.lines.map((l) => ({ ...l, is_selected: choosable(l) ? chosen.has(l.id) : true })), [q.lines, chosen]); // eslint-disable-line react-hooks/exhaustive-deps
  const totals = useMemo(() => computeTotals(lines, q), [lines, q]);
  const problems = useMemo(() => selectionProblems(q.sections, q.lines, chosen), [q.sections, q.lines, chosen]);

  const toggle = (l: PLine, on: boolean) => {
    const s = l.section_id ? sectionOf.get(l.section_id) : undefined;
    setChosen((prev) => {
      const next = new Set(prev);
      if (s?.selection === 'single') for (const other of s.lines) next.delete(other.id);
      if (on) next.add(l.id);
      else next.delete(l.id);
      return next;
    });
  };

  const accept = useMutation({
    mutationFn: () => endpoints.accept({ ...form, selected_line_ids: [...chosen], signature_text: form.signer_name }),
    onSuccess: (v) => { setAccepting(false); onChange(v); },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)),
  });
  const decline = useMutation({
    mutationFn: () => endpoints.decline({ reason: reason || null }),
    onSuccess: (v) => { setDeclining(false); onChange(v); },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)),
  });

  const rel = formatRelativeDays(q.expires_at);
  const status = q.status === 'accepted' || q.status === 'converted'
    ? <Banner tone="pos" icon="check-circle">Accepted{q.accepted_by?.name ? ` by ${q.accepted_by.name}` : ''} on {formatDate(q.accepted_at)}.{q.requires_countersign && !q.countersigned_at ? ' Awaiting countersignature from ' + (q.tenant.name ?? 'the provider') + '.' : ''} A confirmation was emailed to you.</Banner>
    : q.status === 'declined' ? <Banner tone="warn">Declined on {formatDate(q.declined_at)}.{q.declined_reason ? ` Reason: ${q.declined_reason}` : ''}</Banner>
    : q.status === 'expired' ? <Banner tone="error">This quote expired on {formatDate(q.expires_at)}. Ask {q.owner.name ?? q.tenant.name ?? 'your account manager'} for a refreshed quote.</Banner>
    : q.status === 'revised' ? <Banner tone="info">A newer revision of this quote replaces this one.</Banner>
    : null;

  const bucketRows = (label: string, b: typeof totals.one_time) => (
    <dl className="hf-kv" key={label} aria-label={label}>
      <div><dt className="hf-caps">{label}</dt><dd /></div>
      <div><dt>Subtotal</dt><dd className="num" style={{ textAlign: 'right' }}><Money minor={b.subtotal_minor} currency={cur} /></dd></div>
      {minor(b.discount_minor) !== 0n && <div><dt>Discount{q.discount_type === 'percent' ? ` (${Number(q.discount_value)}%)` : ''}</dt><dd className="num" style={{ textAlign: 'right' }}>−<Money minor={b.discount_minor} currency={cur} /></dd></div>}
      {q.tax_mode !== 'none' && <div><dt>Tax{q.tax_mode === 'inclusive' ? ' (included)' : ''}</dt><dd className="num" style={{ textAlign: 'right' }}><Money minor={b.tax_minor} currency={cur} /></dd></div>}
      <div><dt><strong>Total</strong></dt><dd className="num" style={{ textAlign: 'right' }}><strong><Money minor={b.total_minor} currency={cur} /></strong></dd></div>
    </dl>
  );
  const recurringShown = INTERVALS.filter((i) => minor(totals.recurring[i].subtotal_minor) !== 0n);

  return (
    <div className="hf-public">
      <header className="hf-public-head">
        <div>
          <div className="hf-caps">{q.tenant.name ?? 'Quote'}</div>
          <h1>{q.title}</h1>
          <div className="hf-muted hf-mono">{q.ref}{q.revision > 1 ? ` · revision ${q.revision}` : ''}</div>
        </div>
        <div className="hf-public-meta">
          <div><strong>Prepared for</strong><br />{q.client.name}{q.contact.name ? <><br />{q.contact.name}</> : null}</div>
          <div style={{ marginTop: 8 }}><strong>Sent</strong> {formatDate(q.sent_at)}<br /><strong>Valid until</strong> {formatDate(q.expires_at)}{rel && open ? <span className="hf-muted"> ({rel})</span> : null}</div>
          {q.owner.name && <div style={{ marginTop: 8 }}><strong>Your contact</strong><br />{q.owner.name}{q.owner.email ? <><br />{q.owner.email}</> : null}</div>}
        </div>
      </header>
      {status}
      {q.intro && <div style={{ marginTop: 20 }}><RichTextView html={q.intro} /></div>}

      {q.sections.map((s) => (
        <section key={s.id} className="hf-public-section" aria-label={s.title}>
          <h2>{s.title} {s.selection !== 'all' && <Badge tone="info">{s.selection === 'single' ? 'choose one' : 'choose any'}{s.is_optional ? ' · optional' : ''}</Badge>}</h2>
          {s.description && <p className="hf-muted" style={{ margin: '0 0 4px' }}>{s.description}</p>}
          <div role={s.selection === 'single' ? 'radiogroup' : 'group'} aria-label={s.title}>
            {s.lines.map((l) => <PublicLineRow key={l.id} line={l} section={s} cur={cur} selected={lines.find((x) => x.id === l.id)?.is_selected ?? true} choosable={choosable(l)} open={open} onToggle={(on) => toggle(l, on)} />)}
          </div>
        </section>
      ))}
      {q.lines.some((l) => l.section_id === null) && (
        <section className="hf-public-section" aria-label="Items">
          {q.lines.filter((l) => l.section_id === null).map((l) => <PublicLineRow key={l.id} line={l} cur={cur} selected={lines.find((x) => x.id === l.id)?.is_selected ?? true} choosable={choosable(l)} open={open} onToggle={(on) => toggle(l, on)} />)}
        </section>
      )}

      <div className="hf-public-totals" aria-live="polite">
        {(minor(totals.one_time.subtotal_minor) !== 0n || recurringShown.length === 0) && bucketRows('One-time', totals.one_time)}
        {recurringShown.map((i) => bucketRows(`Recurring — ${INTERVAL_LABEL[i]}`, totals.recurring[i]))}
      </div>

      {q.terms && <div className="hf-public-terms"><div className="hf-caps">Terms</div><RichTextView html={q.terms} /></div>}

      <div className="hf-public-actions">
        {open ? (
          <>
            {problems.length > 0 && <span className="hf-error" role="status">{problems[0]}</span>}
            <span className="hf-grow" />
            <Button variant="ghost" onClick={() => setDeclining(true)}>Decline</Button>
            <Button variant="primary" icon="pencil" disabled={problems.length > 0} onClick={() => { setError(null); setAccepting(true); }}>Review &amp; accept</Button>
          </>
        ) : (
          <span className="hf-muted">{tok.used_at ? 'This link has been used.' : q.status === 'sent' || q.status === 'viewed' ? 'Your portal role can view this quote but not accept it.' : 'This quote is no longer open for acceptance.'}</span>
        )}
        {endpoints.onPrint ? (
          <Button variant="ghost" size="sm" icon="file" className="hf-noprint" onClick={endpoints.onPrint}>Print</Button>
        ) : endpoints.printUrl ? (
          <a className="hf-btn ghost sm hf-noprint" href={endpoints.printUrl} target="_blank" rel="noopener noreferrer"><Icon name="file" size={14} /> Print</a>
        ) : null}
      </div>

      <Dialog open={accepting} onClose={() => setAccepting(false)} title={`Accept ${q.ref}`} description="Type your name as your signature. Your name, email, IP address and the time are recorded with the acceptance." locked={accept.isPending}>
        <form className="hf-stack" onSubmit={(e) => { e.preventDefault(); accept.mutate(); }}>
          <div className="hf-kv">
            {recurringShown.length === 0 || minor(totals.one_time.total_minor) !== 0n ? <div><dt>One-time total</dt><dd><Money minor={totals.one_time.total_minor} currency={cur} /></dd></div> : null}
            {recurringShown.map((i) => <div key={i}><dt>Recurring {INTERVAL_SHORT[i]}</dt><dd><Money minor={totals.recurring[i].total_minor} currency={cur} /></dd></div>)}
          </div>
          <Field label="Your full name (signature)" required>
            <Input value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} autoFocus autoComplete="name" />
          </Field>
          <Field label="Your email" required>
            <Input type="email" value={form.signer_email} onChange={(e) => setForm({ ...form, signer_email: e.target.value })} autoComplete="email" />
          </Field>
          <Checkbox label={`I have read and accept the terms of ${q.ref}.`} checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} />
          {error && <Banner tone="error">{error}</Banner>}
          <div className="hf-form-actions">
            <Button variant="ghost" type="button" onClick={() => setAccepting(false)}>Cancel</Button>
            <Button variant="primary" type="submit" busy={accept.isPending} disabled={!form.signer_name.trim() || !form.signer_email.trim() || !form.accept_terms}>Accept quote</Button>
          </div>
        </form>
      </Dialog>
      <Dialog open={declining} onClose={() => setDeclining(false)} title={`Decline ${q.ref}`} locked={decline.isPending}>
        <div className="hf-stack">
          <Field label="Reason (optional)">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {error && <Banner tone="error">{error}</Banner>}
          <div className="hf-form-actions">
            <Button variant="ghost" onClick={() => setDeclining(false)}>Cancel</Button>
            <Button variant="danger" busy={decline.isPending} onClick={() => decline.mutate()}>Decline quote</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export function PublicLineRow({ line: l, section, cur, selected, choosable, open, onToggle }: { line: PLine; section?: PSection; cur: string; selected: boolean; choosable: boolean; open: boolean; onToggle: (on: boolean) => void }) {
  const single = section?.selection === 'single';
  const period = l.kind === 'recurring' && l.recurring_interval ? INTERVAL_LABEL[l.recurring_interval as Interval] : null;
  return (
    <div className={['hf-public-line', !selected && 'dropped'].filter(Boolean).join(' ')}>
      <span>
        {choosable && (
          <input
            type={single ? 'radio' : 'checkbox'}
            name={single ? `section-${section!.id}` : undefined}
            checked={selected}
            disabled={!open}
            aria-label={`${selected ? 'Included' : 'Not included'}: ${l.description}`}
            onChange={(e) => onToggle(single ? true : e.target.checked)}
            onClick={() => { if (single && selected && section?.is_optional) onToggle(false); }}
          />
        )}
      </span>
      <span className="desc">
        {l.description}
        {(l.is_optional || period || Number(l.discount_percent) > 0) && <small>{[l.is_optional ? 'optional' : null, period, Number(l.discount_percent) > 0 ? `${Number(l.discount_percent)}% off` : null].filter(Boolean).join(' · ')}</small>}
      </span>
      <span className="num">{formatQty(l.qty)} ×</span>
      <span className="num"><Money minor={l.unit_price_minor} currency={cur} /></span>
      <span className="num"><strong><Money minor={l.line_total_minor} currency={cur} /></strong></span>
    </div>
  );
}

/** Friendly copy for a failed quote load, shared by both hosts. */
export function quoteLoadError(e: ApiError): { title: string; hint: string | undefined; icon: 'clock' | 'alert-circle' } {
  const title = e.status === 404 ? 'This quote link is not valid' : e.status === 410 ? 'This quote link has expired' : e.status === 429 ? 'Too many requests' : 'Could not load the quote';
  const hint = e.status === 404 ? 'Check the link in your email, or ask your account manager to resend it.' : e.status === 410 ? 'It was superseded by a newer quote or passed its validity date. Ask your account manager for a fresh link.' : e.detail;
  return { title, hint, icon: e.status === 410 ? 'clock' : 'alert-circle' };
}
